import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Download, FileText, FileSpreadsheet, Loader2, TrendingUp, Users as UsersIcon, Building2 } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('user');
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, usersRes, branchesRes, lendersRes] = await Promise.all([
        api.get('/leads'),
        api.get('/users'),
        api.get('/branches'),
        api.get('/lenders'),
      ]);
      setLeads(leadsRes.data);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
      setLenders(lendersRes.data);
    } catch (error) {
      showError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const getUserPerformance = () => {
    return users.map((u) => {
      const userLeads = leads.filter((l) => l.assigned_to === u.id || l.created_by === u.id);
      return {
        name: u.full_name,
        role: u.role.replace('_', ' '),
        total: userLeads.length,
        login: userLeads.filter((l) => l.status === 'login').length,
        sanctioned: userLeads.filter((l) => l.status === 'sanctioned').length,
        disbursed: userLeads.filter((l) => l.status === 'disbursed').length,
        amount: userLeads.filter((l) => l.status === 'disbursed').reduce((s, l) => s + (l.loan_amount || 0), 0),
      };
    });
  };

  const getProductReport = () => {
    const products = ['personal', 'business', 'home', 'auto'];
    return products.map((p) => {
      const productLeads = leads.filter((l) => l.loan_type === p);
      return {
        name: p.charAt(0).toUpperCase() + p.slice(1) + ' Loan',
        total: productLeads.length,
        login: productLeads.filter((l) => l.status === 'login').length,
        sanctioned: productLeads.filter((l) => l.status === 'sanctioned').length,
        disbursed: productLeads.filter((l) => l.status === 'disbursed').length,
        rejected: productLeads.filter((l) => l.status === 'rejected').length,
      };
    });
  };

  const getStatusReport = () => {
    const statuses = ['fresh', 'follow_up', 'login', 'sanctioned', 'disbursed', 'rejected'];
    return statuses.map((s) => ({
      name: s.replace('_', ' ').toUpperCase(),
      count: leads.filter((l) => l.status === s).length,
      amount: leads.filter((l) => l.status === s).reduce((sum, l) => sum + (l.loan_amount || 0), 0),
    }));
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      showError('No data to export');
      return;
    }
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(','),
      ...data.map((row) => headers.map((h) => `"${row[h] ?? ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    showSuccess('Report exported successfully');
  };

  const exportToPDF = (title, data) => {
    if (data.length === 0) {
      showError('No data to export');
      return;
    }
    const headers = Object.keys(data[0]);
    // Escape every untrusted value to prevent XSS via report rows
    const esc = (v) => String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const html = `<!doctype html>
      <html><head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px}
        h1{color:#0B1D3A}
        table{width:100%;border-collapse:collapse;margin-top:20px}
        th,td{border:1px solid #ddd;padding:8px;text-align:left}
        th{background:#2D9CFF;color:white}
        tr:nth-child(even){background:#f9f9f9}
      </style></head>
      <body>
        <h1>Goldpeak Financial Solutions</h1>
        <h2>${esc(title)}</h2>
        <p>Generated: ${esc(new Date().toLocaleString())}</p>
        <table>
          <thead><tr>${headers.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead>
          <tbody>${data.map((row) => `<tr>${headers.map((h) => `<td>${esc(row[h])}</td>`).join('')}</tr>`).join('')}</tbody>
        </table>
        <script>setTimeout(function(){window.print();}, 400);<\/script>
      </body></html>`;
    // Use Blob URL instead of document.write — browser parses HTML safely
    // and we never inject markup via the DOM API.
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank', 'noopener');
    if (!newWindow) {
      showError('Pop-up blocked — allow pop-ups to print the report');
      URL.revokeObjectURL(url);
      return;
    }
    // Revoke the Blob URL once the new window has loaded.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const tabs = [
    { id: 'user', label: 'User Performance', icon: UsersIcon },
    { id: 'product', label: 'Product Wise', icon: FileText },
    { id: 'status', label: 'Status Wise', icon: TrendingUp },
    { id: 'branch', label: 'Branch Wise', icon: Building2 },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const renderTable = () => {
    let data = [];
    let title = '';
    let columns = [];

    if (activeTab === 'user') {
      data = getUserPerformance();
      title = 'User Performance Report';
      columns = [
        { key: 'name', label: 'Name' },
        { key: 'role', label: 'Role' },
        { key: 'total', label: 'Total Leads' },
        { key: 'login', label: 'Login' },
        { key: 'sanctioned', label: 'Sanctioned' },
        { key: 'disbursed', label: 'Disbursed' },
        { key: 'amount', label: 'Disbursed Amount (₹)' },
      ];
    } else if (activeTab === 'product') {
      data = getProductReport();
      title = 'Product Wise Report';
      columns = [
        { key: 'name', label: 'Product' },
        { key: 'total', label: 'Total' },
        { key: 'login', label: 'Login' },
        { key: 'sanctioned', label: 'Sanctioned' },
        { key: 'disbursed', label: 'Disbursed' },
        { key: 'rejected', label: 'Rejected' },
      ];
    } else if (activeTab === 'status') {
      data = getStatusReport();
      title = 'Status Wise Report';
      columns = [
        { key: 'name', label: 'Status' },
        { key: 'count', label: 'Count' },
        { key: 'amount', label: 'Total Amount (₹)' },
      ];
    } else if (activeTab === 'branch') {
      data = branches.map((b) => ({
        name: b.name,
        code: b.code,
        city: b.city,
        manager: b.contact_person,
        status: b.is_active ? 'Active' : 'Inactive',
      }));
      title = 'Branch Wise Report';
      columns = [
        { key: 'name', label: 'Branch Name' },
        { key: 'code', label: 'Code' },
        { key: 'city', label: 'City' },
        { key: 'manager', label: 'Manager' },
        { key: 'status', label: 'Status' },
      ];
    }

    return (
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <div className="flex gap-2">
            <button
              data-testid="export-csv"
              onClick={() => exportToCSV(data, title.toLowerCase().replace(/ /g, '-'))}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm"
            >
              <FileSpreadsheet size={16} />
              Export Excel
            </button>
            <button
              data-testid="export-pdf"
              onClick={() => exportToPDF(title, data)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              <Download size={16} />
              Export PDF
            </button>
          </div>
        </div>

        <div className="overflow-x-auto bg-card border border-border rounded-xl">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center text-muted-foreground">No data available</td>
                </tr>
              ) : (
                data.map((row, idx) => (
                  <tr key={row.id || row.lead_number || row.email || `${columns[0]?.key}-${row[columns[0]?.key]}-${idx}`} className="hover:bg-accent/50">
                    {columns.map((c) => (
                      <td key={c.key} className="px-6 py-4 text-sm text-foreground">
                        {typeof row[c.key] === 'number' && c.key.toLowerCase().includes('amount')
                          ? `₹${row[c.key].toLocaleString('en-IN')}`
                          : row[c.key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Reports</h1>
          <p className="text-muted-foreground">Generate and export business reports</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`tab-report-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {renderTable()}
      </div>
    </DashboardLayout>
  );
};

export default Reports;
